import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PortalPageEnum, BusinessConfigEnum, BusinessModel, BusinessDataModel,
  SubscriptionModel, BusinessHourModel, PhoneAiConfigEnum, SettingsConfigEnum, OperationsConfigEnum, BusinessOnboardingStateModel, BusinessOnboardingWorkspaceModel, BusinessRegistrationProgressModel } from '../models';

export interface BusinessState {
  currentPage: PortalPageEnum;
  currentBusinessConfig: BusinessConfigEnum;
  currentPhoneAiConfig: PhoneAiConfigEnum;
  currentSettingsConfig: SettingsConfigEnum;
  currentOperationsConfig: OperationsConfigEnum;
  business?: BusinessModel;
  subscription?: SubscriptionModel;
  businessHours: BusinessHourModel[];
  onboarding?: BusinessOnboardingStateModel;
  onboardingWorkspace?: BusinessOnboardingWorkspaceModel;
  registrationProgress?: BusinessRegistrationProgressModel;
  liveUpdatedFields: Record<string, number>;

  setupComplete: boolean;

  recordIdToOpen?: number;
  reviewConversationMetaId?: number;
  billingUsageSearch?: string;
}

const normalizePhoneAiConfig = (value: PhoneAiConfigEnum): PhoneAiConfigEnum =>
  value === PhoneAiConfigEnum.Notifications
    ? PhoneAiConfigEnum.CallSettings
    : value;

const initialState: BusinessState = {
  currentPage: PortalPageEnum.Dashboard,
  currentBusinessConfig: BusinessConfigEnum.Availability,
  currentPhoneAiConfig: PhoneAiConfigEnum.CallSettings,
  currentSettingsConfig: SettingsConfigEnum.CoreBusinessDetails,
  currentOperationsConfig: OperationsConfigEnum.WorkItems,
  business: undefined,
  subscription: undefined,
  businessHours: [],
  onboarding: undefined,
  onboardingWorkspace: undefined,
  registrationProgress: undefined,
  liveUpdatedFields: {},

  setupComplete: false,

  recordIdToOpen: undefined,
  reviewConversationMetaId: undefined,
  billingUsageSearch: undefined
};

const shouldPreserveCompletedSetup = (state: BusinessState, nextBusinessId?: number) =>
  state.setupComplete === true &&
  !!state.business?.businessId &&
  !!nextBusinessId &&
  state.business.businessId === nextBusinessId;

const resolveBusinessSetupComplete = (state: BusinessState, business?: BusinessModel | null) =>
  business?.setupComplete === true || shouldPreserveCompletedSetup(state, business?.businessId);

const withResolvedSetupComplete = (state: BusinessState, business?: BusinessModel | null): BusinessModel | undefined => {
  if (!business) {
    return undefined;
  }

  return {
    ...business,
    setupComplete: resolveBusinessSetupComplete(state, business),
  };
};

export const businessSlice = createSlice({
  name: 'business',
  initialState,
  reducers: {
    setBusiness: (state, action: PayloadAction<BusinessModel>) => {
      state.business = withResolvedSetupComplete(state, action.payload);
      state.setupComplete = state.business?.setupComplete === true;
      if (state.onboardingWorkspace) {
        state.onboardingWorkspace.business = state.business;
      }
      return state;
    },
    setBusinessData: (state, action: PayloadAction<BusinessDataModel>) => {
      const data = action.payload;

      state.business = withResolvedSetupComplete(state, data.business);
      //state.addresses = data.addresses ?? [];
      //state.subscription = data.subscription;
      //state.contacts = data.contacts ?? [];
      //state.employees = data.employees ?? [];
      state.businessHours = data.businessHours ?? [];
      state.onboarding = data.onboarding;
      state.onboardingWorkspace = undefined;
      state.registrationProgress = undefined;

      state.setupComplete = state.business?.setupComplete === true;

      return state;
    },
    setSetupComplete: (state, action: PayloadAction<boolean>) => {
      if (!action.payload && state.setupComplete) {
        return state;
      }

      state.setupComplete = action.payload;
      if (state.business) {
        state.business.setupComplete = action.payload;
      }
      if (state.onboardingWorkspace?.business) {
        state.onboardingWorkspace.business.setupComplete = action.payload;
      }
      if (!action.payload && state.onboarding) {
        state.onboarding = {
          ...state.onboarding,
          isCompleted: false,
          status: state.onboarding.status === 'completed' ? 'in_progress' : state.onboarding.status
        };
      }
      return state;
    },
    setOnboardingState: (state, action: PayloadAction<BusinessOnboardingStateModel | undefined>) => {
      state.onboarding = action.payload;
      if (state.onboardingWorkspace) {
        state.onboardingWorkspace.onboarding = action.payload;
      }
      return state;
    },
    setOnboardingWorkspace: (state, action: PayloadAction<BusinessOnboardingWorkspaceModel | undefined>) => {
      const workspace = action.payload
        ? {
          ...action.payload,
          business: withResolvedSetupComplete(state, action.payload.business),
        }
        : undefined;

      state.onboardingWorkspace = workspace;

      if (workspace?.business) {
        state.business = workspace.business;
      }

      if (workspace?.businessHours) {
        state.businessHours = workspace.businessHours;
      }

      if (workspace?.onboarding) {
        state.onboarding = workspace.onboarding;
      }

      if (workspace?.business) {
        state.setupComplete = workspace.business.setupComplete === true;
      }

      return state;
    },
    setRegistrationProgress: (state, action: PayloadAction<BusinessRegistrationProgressModel | undefined>) => {
      state.registrationProgress = action.payload;
      return state;
    },
    markLiveUpdatedFields: (state, action: PayloadAction<string[]>) => {
      const timestamp = Date.now();
      action.payload
        .filter((fieldKey, index, fields) => !!fieldKey && fields.indexOf(fieldKey) === index)
        .forEach((fieldKey) => {
          state.liveUpdatedFields[fieldKey] = timestamp;
        });
      return state;
    },
    goDashboard: (state) => {
      state.currentPage = PortalPageEnum.Dashboard;
      return state;
    },
    goSettings: (state) => {
      state.currentPage = PortalPageEnum.Settings;
      return state;
    },
    goPhoneAi: (state) => {
      state.currentPage = PortalPageEnum.PhoneAi;
      return state;
    },
    goOperations: (state) => {
      state.currentPage = PortalPageEnum.Operations;
      return state;
    },
    goAiConfig: (state) => {
      state.currentPage = PortalPageEnum.AiConfig;
      return state;
    },
    goAiConfigNew: (state) => {
      state.currentPage = PortalPageEnum.AiConfigNew;
      return state;
    },
    goCallReview: (state) => {
      state.currentPage = PortalPageEnum.CallReview;
      return state;
    },
    goBilling: (state) => {
      state.currentPage = PortalPageEnum.Billing;
      return state;
    },
    goBusiness: (state) => {
      state.currentPage = PortalPageEnum.Business;
      return state;
    },
    goBusinessCatalog: (state) => {
      state.currentPage = PortalPageEnum.BusinessCatalog;
      return state;
    },
    goServices: (state) => {
      state.currentPage = PortalPageEnum.Services;
      return state;
    },
    goReceptionist: (state) => {
      state.currentPage = PortalPageEnum.Receptionist;
      return state;
    },
    goCommunications: (state) => {
      state.currentPage = PortalPageEnum.Operations;
      state.currentOperationsConfig = OperationsConfigEnum.CallLog;
      return state;
    },
    goPhoneNumbers: (state) => {
      state.currentPage = PortalPageEnum.PhoneNumbers;
      return state;
    },
    goBookings: (state) => {
      state.currentPage = PortalPageEnum.Operations;
      state.currentOperationsConfig = OperationsConfigEnum.Bookings;
      return state;
    },
    goSkillRunner: (state) => {
      state.currentPage = PortalPageEnum.SkillRunner;
      return state;
    },
    goHelp: (state) => {
      state.currentPage = PortalPageEnum.Help;
      return state;
    },
    goDeepKnowledge: (state) => {
      state.currentPage = PortalPageEnum.DeepKnowledge;
      return state;
    },
    setBusinessType: (state, action: PayloadAction<BusinessConfigEnum>) => {
      state.currentBusinessConfig = action.payload;
      return state;
    },
    setPhoneAiType: (state, action: PayloadAction<PhoneAiConfigEnum>) => {
      state.currentPhoneAiConfig = normalizePhoneAiConfig(action.payload);
      return state;
    },
    setSettingsType: (state, action: PayloadAction<SettingsConfigEnum>) => {
      state.currentSettingsConfig = action.payload;
      return state;
    },
    setOperationsType: (state, action: PayloadAction<OperationsConfigEnum>) => {
      state.currentOperationsConfig = action.payload;
      return state;
    },

    setBusinessHours: (state, action: PayloadAction<BusinessHourModel[]>) => {
      state.businessHours = action.payload;
      if (state.onboardingWorkspace) {
        state.onboardingWorkspace.businessHours = action.payload;
        state.onboardingWorkspace.counts = {
          ...(state.onboardingWorkspace.counts ?? {}),
          businessHours: action.payload.length
        };
      }
      return state;
    },
    setBusinessHour: (state, action: PayloadAction<{ result: BusinessHourModel }>) => {
      const newBusinessHour = action.payload.result;

      // Ensure newBusinessHour is not null or undefined
      if (!newBusinessHour || newBusinessHour.businessHourId == null) {
        console.error('Invalid businessHour in action payload:', newBusinessHour);
        return state;
      }

      const existingIndex = state.businessHours.findIndex(
        (businessHour) => businessHour?.businessHourId === newBusinessHour.businessHourId
      );

      if (existingIndex >= 0) {
        // Update the specific business hour
        state.businessHours[existingIndex] = newBusinessHour;
      } else {
        // Add the new business hour
        state.businessHours.push(newBusinessHour);
      }

      return state;
    },
    removeBusinessHour: (state, action: PayloadAction<{ businessHourId: number }>) => {
      const businessHourId = action.payload.businessHourId;
      state.businessHours = state.businessHours.filter(businessHour => businessHour.businessHourId !== businessHourId);
      return state;
    },
    setRecordIdToOpen: (state, action: PayloadAction<number | undefined>) => {
      state.recordIdToOpen = action.payload;
    },
    setReviewConversationMetaId: (state, action: PayloadAction<number | undefined>) => {
      state.reviewConversationMetaId = action.payload;
    },
    setBillingUsageSearch: (state, action: PayloadAction<string | undefined>) => {
      state.billingUsageSearch = action.payload;
    },
    setLogoutBusiness: (state) => {
      state = initialState;
      return state;
    },
  },
});

export const { setBusinessData, setSetupComplete, setOnboardingState, setOnboardingWorkspace, setRegistrationProgress, goDashboard, goSettings, goPhoneAi, goOperations, goAiConfig, goAiConfigNew, goCallReview, goBilling, goReceptionist, goBusiness, goBusinessCatalog, goServices, goCommunications, goBookings, goPhoneNumbers, setBusiness, setBusinessType, setPhoneAiType, setSettingsType, setOperationsType,
  removeBusinessHour, setBusinessHours, setRecordIdToOpen, setReviewConversationMetaId, setBillingUsageSearch, setLogoutBusiness, goSkillRunner, goHelp, goDeepKnowledge, markLiveUpdatedFields
} = businessSlice.actions;

export default businessSlice.reducer;
